import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDSnackbar from 'components/MDSnackbar';
import MDTypography from 'components/MDTypography';
import TableHeader from 'components/Table/TableHeader';

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { APP_STATE } from 'services/Define';
import * as Func from 'services/Function';
import {
  getCriteriaList,
  updateCriteriaValue,
  fetchCriteriaValues,
  CRITERIA_CODES,
} from 'services/Criteria';

const initHeader = [
  { id: 'code', label: 'รหัส', align: 'left' },
  { id: 'name', label: 'ชื่อ', align: 'left' },
  { id: 'value', label: 'ค่าที่กำหนด', align: 'right' },
  { id: 'description', label: 'คำอธิบาย', align: 'left' },
  { id: 'actions', label: 'จัดการ', align: 'center', sort: false },
];

function Criteria() {
  const mod = 'criteria';
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const [record, setRecord] = useState([]);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title='System Criteria'
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(null)}
      />
    );

    setTimeout(() => {
      setNotify(null);
    }, 3000);
  };

  const loadCriteria = async () => {
    setLoading(true);
    try {
      const data = await getCriteriaList(app.token);
      setRecord(data);
    } catch (error) {
      console.error('Error loading criteria:', error);
      showNotify('ไม่สามารถโหลดข้อมูลได้', 'error');
    }
    setLoading(false);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setEditValue(item.value.toString());
  };

  const handleSave = async (item) => {
    if (!editValue || Number.isNaN(parseFloat(editValue))) {
      showNotify('กรุณากรอกค่าที่ถูกต้อง', 'error');
      return;
    }

    try {
      const success = await updateCriteriaValue(
        app.token,
        item.id,
        item.code,
        parseFloat(editValue),
        item.description || ''
      );

      if (success) {
        showNotify('บันทึกข้อมูลสำเร็จ', 'success');
        setEditingId(null);
        setEditValue('');
        loadCriteria();
        fetchCriteriaValues(app.token);
      } else {
        showNotify('ไม่สามารถบันทึกข้อมูลได้', 'error');
      }
    } catch (error) {
      console.error('Error saving criteria:', error);
      showNotify('เกิดข้อผิดพลาดในการบันทึก', 'error');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValue('');
  };

  const getCriteriaName = (code) => {
    const names = {
      [CRITERIA_CODES.PO_PRICE_TOLERANCE]: 'ส่วนต่างราคา PO ที่ยอมรับได้',
    };
    return names[code] || code;
  };

  const getCriteriaDescription = (code) => {
    const descriptions = {
      [CRITERIA_CODES.PO_PRICE_TOLERANCE]:
        'เปอร์เซ็นต์ส่วนต่างของราคาที่ยอมรับได้ระหว่างราคา PO และราคารับจริง',
    };
    return descriptions[code] || '';
  };

  const renderValueCell = (item) => {
    if (editingId === item.id) {
      return (
        <MDBox display='flex' alignItems='center' gap={1}>
          <TextField
            size='small'
            type='number'
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            InputProps={{
              endAdornment: <InputAdornment position='end'>%</InputAdornment>,
            }}
            sx={{ width: 120 }}
          />
        </MDBox>
      );
    }

    return `${item.value}%`;
  };

  const renderActionCell = (item) => {
    if (editingId === item.id) {
      return (
        <MDBox display='flex' gap={1}>
          <MDButton
            variant='contained'
            color='success'
            size='small'
            onClick={() => handleSave(item)}
          >
            บันทึก
          </MDButton>
          <MDButton variant='outlined' color='secondary' size='small' onClick={handleCancel}>
            ยกเลิก
          </MDButton>
        </MDBox>
      );
    }

    return (
      <MDButton variant='outlined' color='info' size='small' onClick={() => handleEdit(item)}>
        แก้ไข
      </MDButton>
    );
  };

  useEffect(() => {
    loadCriteria();
  }, []);

  return (
    <DashboardLayout>
      <Navbar route={['ระบบ', 'การตั้งค่าเกณฑ์']} />
      <MDBox mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox pt={3} px={3}>
                <MDTypography variant='h6' fontWeight='medium'>
                  การตั้งค่าเกณฑ์ระบบ
                </MDTypography>
                <MDTypography variant='body2' color='text' mb={3}>
                  กำหนดค่าเกณฑ์ต่างๆ ที่ใช้ในการตรวจสอบและควบคุมระบบ
                </MDTypography>
              </MDBox>
              <MDBox pb={3} px={3}>
                <TableContainer>
                  <Table>
                    <TableHeader data={initHeader} />
                    <TableBody>
                      {record.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>{row.code}</TableCell>
                          <TableCell>{getCriteriaName(row.code)}</TableCell>
                          <TableCell align='right'>{renderValueCell(row)}</TableCell>
                          <TableCell>
                            {row.description || getCriteriaDescription(row.code)}
                          </TableCell>
                          <TableCell align='center'>{renderActionCell(row)}</TableCell>
                        </TableRow>
                      ))}
                      {record.length === 0 && !loading && (
                        <TableRow>
                          <TableCell colSpan={5} align='center'>
                            <MDTypography variant='body2' color='text'>
                              ไม่มีข้อมูล
                            </MDTypography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      {notify}
    </DashboardLayout>
  );
}

export default Criteria;
